import {defineStore} from 'pinia'
import {computed, markRaw, ref, shallowRef, watch} from 'vue'
import {
  RemoteTrackPublication,
  Room,
  RoomEvent,
  VideoPresets,
  ConnectionState,
  Track,
  LocalTrackPublication,
  LocalParticipant,
  RemoteParticipant,
  RemoteTrack,
  LocalTrack,
} from 'livekit-client'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {useEventListener} from '@vueuse/core'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {
  IConversationTypeInterfacesMap,
} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {useCallApiStore} from '@toolify/client/src/stores/api/CallApiStore/useCallApiStore'
import userJoinedSound from '@toolify/client/src/assets/sounds/user-joined.wav'
import userLeftSound from '@toolify/client/src/assets/sounds/user-left.wav'
import screenSharingSound from '@toolify/client/src/assets/sounds/screen-sharing.wav'
import {ICallItem} from './types/ICallItem'
import {useCallSettingsStore} from '@toolify/client/src/stores/CallSettingsStore/useCallSettingsStore'
import {ICallJoinOptions} from '@toolify/client/src/modules/call/types/ICallJoinOptions'
import {i18n} from '@toolify/client/src/setup/i18n'
import {getRuntimeEnv} from '@toolify/client/src/stores/AdminRuntimeStore/functions/getRuntimeEnv'

export const useCallItemStore = defineStore('callItem', () => {
  const callApiStore = useCallApiStore()
  const callSettingsStore = useCallSettingsStore()
  const messages = useMessages()

  function getParticipantDisplayName(participant?: RemoteParticipant): string {
    if (!participant) {
      return i18n.global.t('call.someone')
    }
    try {
      const metadata = participant.metadata ? JSON.parse(participant.metadata) : null
      const nick = metadata?.payload?.nick
      if (nick) {
        return nick
      }
    } catch (_err) {
      void _err
    }
    return participant.name || i18n.global.t('call.someone')
  }

  const callItems = shallowRef<ICallItem[]>([])
  const callRoomParticipantsEpoch = ref(0)
  const stopAudioUnblockListenersByRoom = new Map<Room, VoidFunction>()

  function stopAudioUnblockListeners(room: Room) {
    const stopListeners = stopAudioUnblockListenersByRoom.get(room)
    if (!stopListeners) {
      return
    }
    stopListeners()
    stopAudioUnblockListenersByRoom.delete(room)
  }

  function listenForAudioUnblockGesture(room: Room) {
    if (stopAudioUnblockListenersByRoom.has(room)) {
      return
    }
    const retryPlayback = () => {
      void ensureAudioPlayback(room)
    }
    const stopListeners = [
      useEventListener(window, 'pointerdown', retryPlayback),
      useEventListener(window, 'keydown', retryPlayback),
      useEventListener(window, 'touchstart', retryPlayback),
    ]
    stopAudioUnblockListenersByRoom.set(room, () => {
      stopListeners.forEach(stopListener => stopListener())
    })
  }

  async function ensureAudioPlayback(room: Room) {
    if (room.canPlaybackAudio) {
      stopAudioUnblockListeners(room)
      return
    }
    try {
      await room.startAudio()
    } catch (_err) {
      void _err
    }
    if (room.canPlaybackAudio) {
      stopAudioUnblockListeners(room)
      return
    }
    listenForAudioUnblockGesture(room)
  }

  const activeCallItem = computed<ICallItem | null>(() => {
    return callItems.value.find(callItem => {
      return callItem.roomConnectionState !== ConnectionState.Disconnected
    }) || null
  })

  function ensureCallItem<T extends ConversationType>(
    conversationType: ConversationType,
    conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
  ) {
    const existingCallItem = callItems.value.find(callItem => {
      return callItem.conversationType === conversationType
        && JSON.stringify(callItem.conversationTypePayload) === JSON.stringify(conversationTypePayload)
    })

    if (!existingCallItem) {
      addCallItem(conversationType, conversationTypePayload)
    }
  }

  function addCallItem<T extends ConversationType>(
    conversationType: ConversationType,
    conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
  ) {
    const room = markRaw(new Room({
      adaptiveStream: true,
      dynacast: true,
      stopLocalTrackOnUnpublish: false,
      videoCaptureDefaults: {
        resolution: VideoPresets.h1080.resolution,
      },
    }))
    const callItemId = Symbol()
    callItems.value = callItems.value.concat([{
      id: callItemId,
      room,
      conversationType,
      conversationTypePayload,
      roomConnectionState: ConnectionState.Disconnected,
    }])

    function onConnectionStateChanged(state: ConnectionState) {
      callItems.value = callItems.value.map(callItem => {
        if(callItem.id === callItemId) {
          const wasConnected = callItem.roomConnectionState === ConnectionState.Connected
          return {
            ...callItem,
            roomConnectionState: state,
            connectedAt: state === ConnectionState.Connected && !wasConnected
              ? Date.now()
              : callItem.connectedAt,
            lastSessionEndedAt: state !== ConnectionState.Connected && wasConnected
              ? Date.now()
              : callItem.lastSessionEndedAt,
          }
        }
        return callItem
      })

      if (state === ConnectionState.Connected) {
        callRoomParticipantsEpoch.value += 1
        callSettingsStore.setCallActive(true)
        void ensureAudioPlayback(room)
      }

      if (state === ConnectionState.Disconnected) {
        stopAudioUnblockListeners(room)
        const hasAnyActiveCall = callItems.value.some(callItem => {
          return callItem.roomConnectionState !== ConnectionState.Disconnected
        })
        if (!hasAnyActiveCall) {
          callSettingsStore.setCallActive(false)
        }
      }
    }

    async function onTrackSubscribed(
      track: RemoteTrack<Track.Kind>,
      publication: RemoteTrackPublication,
      participant,
    ) {
      if(track.source === Track.Source.ScreenShare) {
        const audio = new Audio(screenSharingSound)
        await audio.play()
      }
    }

    async function onLocalTrackPublished(
      publication: LocalTrackPublication,
      _participant: LocalParticipant,
    ) {
      if(publication.track.source === Track.Source.ScreenShare) {
        const audio = new Audio(screenSharingSound)
        await audio.play()
      }
    }

    async function onParticipantConnected() {
      callRoomParticipantsEpoch.value += 1
      const audio = new Audio(userJoinedSound)
      await audio.play()

      // Sync our status with the new joiner
      syncParticipantMetadata()
    }

    async function onParticipantDisconnected() {
      callRoomParticipantsEpoch.value += 1
      const audio = new Audio(userLeftSound)
      await audio.play()
    }

    function onAudioPlaybackStatusChanged() {
      void ensureAudioPlayback(room)
    }

    function onDataReceived(payload: Uint8Array, participant?: RemoteParticipant) {
      try {
        const message = JSON.parse(new TextDecoder().decode(payload))
        if (message.type !== 'mic_unmute_request' || message.targetIdentity !== room.localParticipant.identity) {
          return
        }
        // a newer request replaces the card, so it always names the latest asker
        messages.info(i18n.global.t('call.unmuteRequestToast', {name: getParticipantDisplayName(participant)}), {
          duration: 10000,
          replaceKey: 'call.unmuteRequest',
          actions: [{
            label: i18n.global.t('call.unmuteMicrophone'),
            kind: MessageActionKindType.Primary,
            onClick: () => {
              callSettingsStore.setMicrophoneEnabled(true)
            },
          }],
        })
      } catch (_err) {
        void _err
      }
    }

    room.on(RoomEvent.ConnectionStateChanged, onConnectionStateChanged)
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed)
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected)
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected)
    room.on(RoomEvent.Connected, onParticipantConnected)
    room.on(RoomEvent.Disconnected, onParticipantDisconnected)
    room.on(RoomEvent.LocalTrackPublished, onLocalTrackPublished)
    room.on(RoomEvent.AudioPlaybackStatusChanged, onAudioPlaybackStatusChanged)
    room.on(RoomEvent.DataReceived, onDataReceived)
  }

  async function join<T extends ConversationType>(
    conversationType: T,
    conversationTypePayload: IConversationTypeInterfacesMap[T]['conversationTypePayload'],
    joinOptions?: ICallJoinOptions,
  ) {
    if (typeof joinOptions?.enableCamera === 'boolean') {
      callSettingsStore.setCameraEnabled(joinOptions.enableCamera)
    }
    if (typeof joinOptions?.enableMicrophone === 'boolean') {
      callSettingsStore.setMicrophoneEnabled(joinOptions.enableMicrophone)
    }

    callSettingsStore.setCallActive(true)
    let callItem = callItems.value.find(callItem => {
      return callItem.conversationType === conversationType
        && JSON.stringify(callItem.conversationTypePayload) === JSON.stringify(conversationTypePayload)
    })
    try {
      const {accessToken, call} = await callApiStore.getCallAccessToken(conversationType, conversationTypePayload)
      callItem = callItems.value.find(item => {
        return item.conversationType === conversationType
          && JSON.stringify(item.conversationTypePayload) === JSON.stringify(conversationTypePayload)
      })

      if (!callItem) {
        throw new Error('Call item not found')
      }

      callItems.value = callItems.value.map(item => {
        if (item.id === callItem.id) {
          return {
            ...item,
            call,
          }
        }
        return item
      })
      callItem = callItems.value.find(item => {
        return item.conversationType === conversationType
          && JSON.stringify(item.conversationTypePayload) === JSON.stringify(conversationTypePayload)
      })

      await callItem.room.connect(getRuntimeEnv().LiveKitUrl, accessToken)
      syncParticipantMetadata()
      await callSettingsStore.ensureCameraTrack()
      await callSettingsStore.ensureMicrophoneTrack()
      await publishLocalTracks(callItem.room)
      await disconnectOtherRooms(callItem.id)
    } catch (error) {
      if (callItem && callItem.room.state !== ConnectionState.Disconnected) {
        await callItem.room.disconnect()
      }
      const hasAnyActiveCall = callItems.value.some(item => {
        return item.roomConnectionState !== ConnectionState.Disconnected
      })
      if (!hasAnyActiveCall) {
        callSettingsStore.setCallActive(false)
      }
      throw error
    }
  }

  async function disconnect() {
    if(activeCallItem.value) {
      await unpublishLocalTracks(activeCallItem.value.room)
      await activeCallItem.value.room?.disconnect()
    }
    callSettingsStore.setCallActive(false)
  }

  async function disconnectOtherRooms(exceptCallItemId: symbol) {
    const otherConnectedCallItems = callItems.value.filter(item => {
      return item.id !== exceptCallItemId && item.room.state !== ConnectionState.Disconnected
    })
    const disconnectResults = await Promise.allSettled(otherConnectedCallItems.map(async otherCallItem => {
      await unpublishLocalTracks(otherCallItem.room)
      await otherCallItem.room.disconnect(false)
    }))
    const disconnectErrors = disconnectResults
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map(result => result.reason)
    if (disconnectErrors.length) {
      throw new AggregateError(disconnectErrors, 'Failed to disconnect previously joined call rooms')
    }
  }

  async function publishMicrophoneMessage(payload: Record<string, unknown>): Promise<boolean> {
    const callItem = activeCallItem.value
    if (!callItem || callItem.roomConnectionState !== ConnectionState.Connected) {
      return false
    }
    try {
      const data = new TextEncoder().encode(JSON.stringify(payload))
      await callItem.room.localParticipant.publishData(data, {reliable: true})
      return true
    } catch (_err) {
      return false
    }
  }

  async function sendUnmuteRequest(targetIdentity: string) {
    const isSent = await publishMicrophoneMessage({
      type: 'mic_unmute_request',
      targetIdentity,
    })
    if (!isSent) {
      messages.error(i18n.global.t('call.unmuteRequestFailed'))
      return
    }
    messages.info(i18n.global.t('call.unmuteRequestSent'))
  }

  function syncParticipantMetadata() {
    const callItem = activeCallItem.value
    if (callItem?.roomConnectionState === ConnectionState.Connected) {
      const participant = callItem.room.localParticipant
      
      // Broadcast via Data Channel (since metadata/attributes are blocked by server)
      try {
        const payload = {
          type: 'deafen_status',
          isDeafened: !callSettingsStore.isOutputAudioEnabled,
        }
        const encoder = new TextEncoder()
        const data = encoder.encode(JSON.stringify(payload))
        participant.publishData(data, {reliable: true})
      } catch (_err) {
        // catch error
      }
    }
  }

  async function unpublishLocalTracks(room: Room) {
    const participant = room.localParticipant
    const unpublishPromises = []
    for (const pub of participant.trackPublications.values()) {
      if (!pub.track) {
        continue
      }
      if (pub.source === Track.Source.Camera || pub.source === Track.Source.Microphone) {
        unpublishPromises.push(participant.unpublishTrack(pub.track as LocalTrack, false))
      }
    }
    await Promise.all(unpublishPromises)
  }

  async function publishLocalTracks(room: Room) {
    const participant = room.localParticipant
    await unpublishLocalTracks(room)

    const cameraTrack = callSettingsStore.getCameraTrack()
    const microphoneTrack = callSettingsStore.getMicrophoneTrack()
    const publishedTracks = new Set(
      [...participant.trackPublications.values()].map(publication => publication.track),
    )

    if (cameraTrack && !publishedTracks.has(cameraTrack)) {
      await participant.publishTrack(cameraTrack)
    }
    if (microphoneTrack && !publishedTracks.has(microphoneTrack)) {
      await participant.publishTrack(microphoneTrack)
    }
  }

  async function syncActiveCamera() {
    const callItem = activeCallItem.value
    if (!callItem || callItem.roomConnectionState !== ConnectionState.Connected) {
      return
    }
    const participant = callItem.room.localParticipant
    const desiredCameraTrack = callSettingsStore.isCameraEnabled ? callSettingsStore.getCameraTrack() : null
    for (const pub of participant.trackPublications.values()) {
      if (pub.track && pub.source === Track.Source.Camera && pub.track !== desiredCameraTrack) {
        await participant.unpublishTrack(pub.track as LocalTrack)
      }
    }
    if (desiredCameraTrack) {
      const alreadyPublished = [...participant.trackPublications.values()].some(pub => pub.track === desiredCameraTrack)
      if (!alreadyPublished) {
        await participant.publishTrack(desiredCameraTrack)
      }
    }
  }

  async function syncActiveMicrophone() {
    const callItem = activeCallItem.value
    if (!callItem || callItem.roomConnectionState !== ConnectionState.Connected) {
      return
    }
    const participant = callItem.room.localParticipant
    const microphoneTrack = callSettingsStore.getMicrophoneTrack()
    const micPub = [...participant.trackPublications.values()].find(p => p.source === Track.Source.Microphone)
    const publishedTrack = micPub?.track ?? null

    if (publishedTrack !== microphoneTrack) {
      if (publishedTrack) {
        await participant.unpublishTrack(publishedTrack as LocalTrack)
      }
      if (microphoneTrack) {
        await participant.publishTrack(microphoneTrack)
      }
    }

    if (microphoneTrack) {
      if (callSettingsStore.isMicrophoneEnabled) {
        await microphoneTrack.unmute()
      } else {
        await microphoneTrack.mute()
      }
    }
  }

  watch(() => [callSettingsStore.cameraTrackVersion, callSettingsStore.isCameraEnabled], () => {
    syncActiveCamera().catch(() => undefined)
  })

  watch(() => [callSettingsStore.microphoneTrackVersion, callSettingsStore.isMicrophoneEnabled], () => {
    syncActiveMicrophone().catch(() => undefined)
  })

  watch(() => callSettingsStore.isOutputAudioEnabled, () => {
    syncParticipantMetadata()
  }, {immediate: true})

  function getCallItem(
    conversationType: ConversationType,
    conversationTypePayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload'],
  ) {
    return callItems.value.find(callItem => {
      return callItem.conversationType === conversationType
        && JSON.stringify(callItem.conversationTypePayload) === JSON.stringify(conversationTypePayload)
    }) || null
  }

  return {
    callItems,
    callRoomParticipantsEpoch,
    ensureCallItem,
    getCallItem,
    activeCallItem,
    join,
    disconnect,
    sendUnmuteRequest,
  }
})
